import React from 'react';
import { useDispatch } from 'react-redux';
import { showSuccessBanner } from '@utils/application';
import { Body5, Container } from '@ui';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { GeneratorForm } from './GeneratorForm';
import { useGenerateAgentScript } from './useGenerateAgentScript';
import { openAgentScriptInFlowEditor } from './openAgentScriptInFlowEditor';

const OPEN_TAB_DELAY_MS = 3000;

/**
 * Generate-a-new-script panel: collects a name + description, creates the
 * script, and opens it in the flow editor (with a manual fallback link in case
 * the browser blocks the auto-opened tab).
 */
export const GenerateAgentScriptPanel = () => {
  const dispatch = useDispatch();

  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [generatedScript, setGeneratedScript] = React.useState(null);

  const { generate, isGenerating, error, nameError } = useGenerateAgentScript();

  // Once a script is generated, announce it and open the flow editor after a
  // short delay so the user can read the success message first.
  React.useEffect(() => {
    if (!generatedScript) return undefined;

    const timeoutId = setTimeout(() => {
      showSuccessBanner(`Opening "${generatedScript.script_name}" in a new tab...`, null, dispatch);
      openAgentScriptInFlowEditor(generatedScript.script_name);
    }, OPEN_TAB_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [generatedScript, dispatch]);

  const handleSubmit = async () => {
    const record = await generate({ name, description });
    if (record) {
      showSuccessBanner('Agent script generated. It will open in a new tab shortly.', null, dispatch);
      setGeneratedScript(record);
    }
  };

  return (
    <Container>
      <Container bottom={24}>
        <Body5 isLowContrast>Describe the agent you want and we'll generate a runnable agent script for you.</Body5>
      </Container>

      <GeneratorForm
        name={name}
        description={description}
        nameError={nameError}
        error={error}
        isGenerating={isGenerating}
        onNameChange={setName}
        onDescriptionChange={setDescription}
        onSubmit={handleSubmit}
      />

      {generatedScript && (
        <Container top={32}>
          <Body5 bold>"{generatedScript.script_name}" is ready.</Body5>
          <Container top={8}>
            <Body5 isLowContrast>If the new tab didn't open, use the button below.</Body5>
          </Container>
          <Container top={16}>
            <SkeletonButton onClick={() => openAgentScriptInFlowEditor(generatedScript.script_name)}>
              Open in Flow Editor
            </SkeletonButton>
          </Container>
        </Container>
      )}
    </Container>
  );
};
